import ReactDom from 'react-dom';
import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import useHasMounted from '@/hooks/useHasMounted';
import Modal from '@/widgets/Modal';
import { useCommentStore } from '@/components/Comments/CommentContext';
import CommentDeleteModal from '@/domains/Modals/Comments/CommentDeleteModal';
import CommentLoadingModal from '@/domains/Modals/Comments/CommentLoadingModal';
import CommentFailureModal from '@/domains/Modals/Comments/CommentFailureModal';
import { CommentType } from '@/types/Content/Comment';

type CommentModalsProps = {
  onDelete?: (comment: CommentType) => void;
};

function CommentModals(props: CommentModalsProps): JSX.Element {
  const { onDelete } = props;
  const isMounted = useHasMounted();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    isDeleting,
    errors,
    deletionQueue,
    clearErrors,
    cancelDeletion,
  } = useCommentStore();
  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    clearErrors();
    setIsModalOpen(false);
    cancelDeletion();
  };

  useEffect(() => {
    if ([deletionQueue?.length, isDeleting, errors?.size].some(Boolean)) {
      openModal();
    } else {
      closeModal();
    }
  }, [deletionQueue?.length, isDeleting, errors?.size]);

  if (!isMounted) return null;

  let modalContent = null;

  if (deletionQueue.length) {
    modalContent = <CommentDeleteModal onDelete={onDelete} />;
  } else if (isDeleting) {
    modalContent = <CommentLoadingModal />;
  } else if (errors.size) {
    modalContent = <CommentFailureModal />;
  } else {
    modalContent = null;
  }

  return ReactDom.createPortal(
    <Modal isOpen={isModalOpen} handleClose={closeModal}>
      {modalContent}
    </Modal>,
    document.getElementById('ModalPortal'),
  );
}

export default observer(CommentModals);
